import React from 'react';
import {
	formatLocation,
	getCandidateOption,
	getImportRowIdentity,
} from './importReview.model';
import {
	getBulkAssignmentOptionKey,
	getBulkAssignmentOptions,
	getBulkContractFrequencyOptionKey,
	getBulkContractFrequencyOptions,
	getBulkLocationOptionKey,
	getBulkLocationOptions,
	getBulkServiceOptionKey,
	getBulkServiceOptions,
	getSelectedImportOrders,
} from './importBulkActions.model';
import styles from './ImportReview.module.css';

const noop = () => {};

const getAssignmentLabel = option =>
	[
		option.name || `Técnico #${option.id}`,
		option.workCode,
		option.entityName ||
			(option.entityId !== undefined
				? `Entidad #${option.entityId}`
				: undefined),
	]
		.filter(Boolean)
		.join(' · ');

const getServiceLabel = option => `${option.name} · ID ${option.id}`;

const getLocationLabel = option => formatLocation(getCandidateOption(option));

const getContractFrequencyLabel = option => {
	const count = option.targetRowIds.length;
	return `${option.existing?.name || `Frecuencia #${option.existing?.frequencyId}`} → ${
		option.requested?.name || `Frecuencia #${option.requested?.frequencyId}`
	} (${count} ${count === 1 ? 'orden' : 'órdenes'})`;
};

const BulkSelect = ({ action, disabled, onSelect }) => {
	const helpId = `bulk-${action.key}-help`;
	return (
		<label className={styles.bulkActionField}>
			<span>{action.label}</span>
			<select
				value=''
				disabled={disabled || Boolean(action.disabledReason)}
				aria-describedby={helpId}
				onChange={event =>
					onSelect(action, event.target.value, event.currentTarget)
				}
			>
				<option value=''>Seleccionar…</option>
				{action.options.map(option => (
					<option key={action.getKey(option)} value={action.getKey(option)}>
						{action.getLabel(option)}
					</option>
				))}
			</select>
			<small id={helpId}>
				{action.disabledReason ||
					action.helpText ||
					'Solo se muestran opciones válidas para todas las filas seleccionadas.'}
			</small>
		</label>
	);
};

const ImportBulkActions = ({
	orders,
	selectedRowIds,
	onSelectedRowIdsChange,
	assignedTechIdField,
	serviceIdField,
	contractFrequencyIdField,
	locationFields,
	zoneFields,
	onApplyAssignment,
	onApplyService,
	onApplyZone,
	onApplyFrequency,
	onPendingChange = noop,
	isBusy = false,
}) => {
	const [pendingAction, setPendingAction] = React.useState();
	const [announcement, setAnnouncement] = React.useState('');
	const confirmationRef = React.useRef(null);
	const confirmationPanelRef = React.useRef(null);
	const backgroundRef = React.useRef(null);
	const triggerRef = React.useRef(null);
	const toolbarTitleRef = React.useRef(null);
	const shouldRestoreFocusRef = React.useRef(false);
	const wasBusyRef = React.useRef(isBusy);
	const selectedOrders = getSelectedImportOrders(orders, selectedRowIds);
	const selectionCount = selectedOrders.length;
	const assignmentAvailability = getBulkAssignmentOptions(
		selectedOrders,
		assignedTechIdField,
	);
	const serviceAvailability = serviceIdField
		? getBulkServiceOptions(selectedOrders)
		: {
				options: [],
				disabledReason: 'La plantilla no incluye el campo de servicio.',
			};
	const frequencyAvailability = getBulkContractFrequencyOptions(orders);
	const actions = [
		{
			key: 'assignment',
			label: 'Técnico',
			...assignmentAvailability,
			getKey: getBulkAssignmentOptionKey,
			getLabel: getAssignmentLabel,
			onApply: onApplyAssignment,
		},
		{
			key: 'service',
			label: 'Servicio',
			...serviceAvailability,
			getKey: getBulkServiceOptionKey,
			getLabel: getServiceLabel,
			onApply: onApplyService,
		},
		...(contractFrequencyIdField?.show === true
			? [
					{
						key: 'contract-frequency',
						label: 'Frecuencia contractual',
						...frequencyAvailability,
						getKey: getBulkContractFrequencyOptionKey,
						getLabel: getContractFrequencyLabel,
						getTargetRowIds: option => option.targetRowIds,
						helpText:
							'Se agrupan todas las órdenes pendientes que comparten la misma transición de frecuencia.',
						onApply: onApplyFrequency,
					},
				]
			: []),
		...(zoneFields || []).map(zoneIdField => ({
			key: `location-${zoneIdField.id}`,
			label:
				zoneIdField.id === 'contractZoneId'
					? 'Zona de contrato'
					: zoneIdField.id === 'orderZoneId'
						? 'Zona de orden'
						: zoneIdField.name || 'Zona',
			...getBulkLocationOptions(selectedOrders, locationFields, zoneIdField),
			fieldId: zoneIdField.id,
			getKey: getBulkLocationOptionKey,
			getLabel: getLocationLabel,
			onApply: onApplyZone,
		})),
	];
	const currentAction = pendingAction
		? actions.find(action => action.key === pendingAction.actionKey)
		: undefined;
	const currentOption = currentAction?.options.find(
		option => currentAction.getKey(option) === pendingAction.optionKey,
	);
	const getActionTargetRowIds = (action, option) =>
		(action?.getTargetRowIds
			? action.getTargetRowIds(option)
			: selectedOrders.map(order => getImportRowIdentity(order))
		).map(String);

	React.useEffect(() => {
		if (pendingAction && confirmationRef.current)
			confirmationRef.current.focus();
	}, [pendingAction]);

	React.useEffect(() => {
		const background = backgroundRef.current;
		if (!background) return undefined;
		if (pendingAction) background.setAttribute('inert', '');
		else background.removeAttribute('inert');
		return () => background.removeAttribute('inert');
	}, [pendingAction]);

	React.useEffect(() => {
		if (isBusy && !wasBusyRef.current) setAnnouncement('');
		wasBusyRef.current = isBusy;
	}, [isBusy]);

	React.useEffect(() => {
		if (pendingAction || !shouldRestoreFocusRef.current) return;
		shouldRestoreFocusRef.current = false;
		const trigger = triggerRef.current;
		if (trigger?.isConnected && !trigger.disabled) trigger.focus();
		else toolbarTitleRef.current?.focus();
	}, [pendingAction]);

	const closeConfirmation = () => {
		shouldRestoreFocusRef.current = true;
		onPendingChange(false);
		setPendingAction(undefined);
	};

	React.useEffect(() => {
		if (!pendingAction) return;
		const currentIds =
			currentAction && currentOption
				? getActionTargetRowIds(currentAction, currentOption).sort()
				: [];
		const pendingIds = pendingAction.targetRowIds.slice().sort();
		if (
			currentIds.length !== pendingIds.length ||
			currentIds.some((rowId, index) => rowId !== pendingIds[index])
		)
			closeConfirmation();
	}, [pendingAction, selectedOrders]);

	const queueAction = (action, optionKey, trigger) => {
		const option = action.options.find(
			candidate => action.getKey(candidate) === optionKey,
		);
		if (!option) return;
		triggerRef.current = trigger;
		setAnnouncement('');
		onPendingChange(true);
		setPendingAction({
			actionKey: action.key,
			optionKey,
			targetRowIds: getActionTargetRowIds(action, option),
		});
	};

	const onConfirmationKeyDown = event => {
		if (event.key === 'Escape') {
			event.preventDefault();
			closeConfirmation();
			return;
		}
		if (event.key !== 'Tab') return;
		const focusable = Array.from(
			confirmationPanelRef.current?.querySelectorAll(
				'button:not([disabled])',
			) || [],
		);
		if (!focusable.length) {
			event.preventDefault();
			return;
		}
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (
			event.shiftKey &&
			(document.activeElement === first ||
				document.activeElement === confirmationRef.current)
		) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	const confirmAction = () => {
		if (!currentAction || !currentOption || isBusy) return;
		const applied = currentAction.onApply?.({
			option: currentOption,
			fieldId: currentAction.fieldId,
			targetRowIds: pendingAction.targetRowIds,
		});
		if (applied !== false)
			setAnnouncement(
				`${currentAction.label}: ${currentAction.getLabel(
					currentOption,
				)} quedó preparado para ${pendingAction.targetRowIds.length} ${
					pendingAction.targetRowIds.length === 1 ? 'orden' : 'órdenes'
				}. Pulsa “Validar cambios” antes de importar.`,
			);
		closeConfirmation();
	};

	return (
		<section
			className={styles.bulkActions}
			aria-labelledby='bulk-actions-title'
		>
			<div
				ref={backgroundRef}
				className={styles.bulkActionsContent}
				aria-hidden={pendingAction ? 'true' : undefined}
			>
				<div className={styles.bulkActionsHeader}>
					<div>
						<strong id='bulk-actions-title' ref={toolbarTitleRef} tabIndex='-1'>
							Acciones masivas
						</strong>
						<span aria-live='polite'>
							{selectionCount}{' '}
							{selectionCount === 1
								? 'orden seleccionada'
								: 'órdenes seleccionadas'}
						</span>
					</div>
					<button
						type='button'
						className={styles.secondaryButton}
						disabled={isBusy || Boolean(pendingAction) || selectionCount === 0}
						onClick={() => onSelectedRowIdsChange([])}
					>
						Limpiar selección
					</button>
				</div>
				<p className={styles.bulkActionsHint}>
					Selecciona filas con las casillas de la tabla. Los cambios se preparan
					en esta misma vista previa y no se guardan hasta importar. Las
					frecuencias pendientes se agrupan automáticamente por transición.
				</p>
				<div className={styles.bulkActionGrid}>
					{actions.map(action => (
						<BulkSelect
							key={action.key}
							action={action}
							disabled={isBusy || Boolean(pendingAction)}
							onSelect={queueAction}
						/>
					))}
				</div>
			</div>

			{pendingAction && (
				<div className={styles.bulkConfirmationBackdrop}>
					<div
						ref={confirmationPanelRef}
						className={styles.bulkConfirmation}
						role='alertdialog'
						aria-modal='true'
						aria-labelledby='bulk-confirmation-title'
						aria-describedby='bulk-confirmation-description'
						onKeyDown={onConfirmationKeyDown}
					>
						<strong
							id='bulk-confirmation-title'
							ref={confirmationRef}
							tabIndex='-1'
						>
							Confirmar cambio masivo
						</strong>
						<p id='bulk-confirmation-description'>
							<strong>{currentAction?.label}:</strong>{' '}
							{currentOption
								? currentAction.getLabel(currentOption)
								: 'Opción vencida'}
							. Se modificará la vista previa de{' '}
							{pendingAction.targetRowIds.length}{' '}
							{pendingAction.targetRowIds.length === 1 ? 'orden' : 'órdenes'}.
						</p>
						<p>
							Después debes pulsar <strong>“Validar cambios”</strong>. Si alguna
							fila dejó de ser compatible, no se aplicará ninguna.
						</p>
						{currentAction?.key === 'contract-frequency' && (
							<p>
								Este paso confirma solo el cambio de frecuencia. Cualquier otra
								incidencia de las órdenes permanecerá pendiente.
							</p>
						)}
						<div className={styles.bulkConfirmationActions}>
							<button
								type='button'
								className={styles.secondaryButton}
								disabled={isBusy}
								onClick={closeConfirmation}
							>
								Cancelar
							</button>
							<button
								type='button'
								className={styles.primaryButton}
								disabled={isBusy || !currentOption}
								onClick={confirmAction}
							>
								Aplicar a {pendingAction.targetRowIds.length}
							</button>
						</div>
					</div>
				</div>
			)}
			{announcement && (
				<p
					className={styles.bulkActionFeedback}
					role='status'
					aria-live='polite'
				>
					{announcement}
				</p>
			)}
		</section>
	);
};

export default ImportBulkActions;
